/**
 * ISO 4217 currencies for the exchange-rate picker (HS2-ZJ8P8N). The browser already ships the
 * list (`Intl.supportedValuesOf('currency')`) and each code's display name, so nothing is
 * hard-coded; a short fallback covers engines without it.
 */
const FALLBACK_CODES = ['AED', 'CNY', 'EUR', 'GBP', 'INR', 'JPY', 'SAR', 'USD'];

export interface CurrencyChoice {
  value: string;
  label: string;
  /** Why the currency cannot be added again: it is the base currency or already configured. */
  unavailable?: 'Base currency' | 'Already in use';
}

/**
 * Every known currency as "CODE — Name". The base currency and those already configured stay in
 * the list, marked unavailable, so a search for them explains why they cannot be added
 * (HS2-5GWEXJ).
 */
export function currencyChoices(baseCurrency: string, configured: readonly string[]): CurrencyChoice[] {
  const codes = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('currency') : FALLBACK_CODES;
  const names = new Intl.DisplayNames(undefined, { type: 'currency' });
  const base = baseCurrency.toUpperCase();
  const inUse = new Set(configured.map((code) => code.toUpperCase()));
  const known = new Set(codes);
  // A configured code the engine does not list still shows as in use.
  const all = [...codes, ...[base, ...inUse].filter((code) => !known.has(code)).sort()];
  return all.map((code) => {
    const choice: CurrencyChoice = { value: code, label: `${code} — ${names.of(code) ?? code}` };
    if (code === base) choice.unavailable = 'Base currency';
    else if (inUse.has(code)) choice.unavailable = 'Already in use';
    return choice;
  });
}

/** Match currency codes and display names without changing their browser-provided order. */
export function filterCurrencyChoices(choices: readonly CurrencyChoice[], query: string): CurrencyChoice[] {
  const term = query.trim().toLocaleLowerCase();
  return choices.filter((choice) => choice.label.toLocaleLowerCase().includes(term));
}

/** Show up to four decimals without turning a small positive rate into zero. */
export function formatFxDisplayRate(rate: number | undefined): string {
  if (rate === undefined || !Number.isFinite(rate) || rate <= 0) return '—';
  const rounded = Number(rate.toFixed(4));
  return rounded === 0 ? '<0.0001' : String(rounded);
}

/** Configured currency codes in a stable display order: USD and CNY first, then alphabetical. */
export function orderedCurrencyCodes(codes: Iterable<string>): string[] {
  const priority = ['USD', 'CNY'];
  return [...codes].sort((a, b) => {
    const pa = priority.indexOf(a);
    const pb = priority.indexOf(b);
    if (pa !== -1 || pb !== -1) return (pa === -1 ? 99 : pa) - (pb === -1 ? 99 : pb);
    return a.localeCompare(b);
  });
}
